import { useState } from "react"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useTranslationUpsertMutation } from "@/v2-backoffice-client/translation/editor/translation.upsert.mutation"

export type TranslationFallbackKeepCodeButtonProps = {
  code: string
  codeName: string
  codeDescription: string
  /** The DB has no en-GB row at all, so this adopts rather than reverts. */
  missingInDb: boolean
  onResolved(): void
}

// Writes the CODE text into the DB's en-GB row: for a diff this reverts the
// DB edit; for a missing en-GB row it adopts the code text. Either way both
// sides become equal and the row leaves the fallback view.
//
// Confirms inline rather than through ConfirmButton's popover: the button
// sits inside an expanded table row that is itself already a popover-free
// detail surface, and the question needs the surrounding sentence.
function TranslationFallbackKeepCodeButton(
  props: TranslationFallbackKeepCodeButtonProps,
) {
  const { code, codeName, codeDescription, missingInDb, onResolved } = props

  const [upsert, upsertState] = useTranslationUpsertMutation()
  const [isArmed, setIsArmed] = useState(false)

  async function onKeepCode() {
    try {
      const res = await upsert({
        variables: {
          input: {
            Code: code,
            LanguageCode: "en-GB",
            Name: codeName,
            Description: codeDescription || null,
            ApplyToLinked: false,
          },
        },
        refetchQueries: ["TranslationFallbackQuery", "TranslationCatalogQuery"],
      })
      if (res.data?.upsertTranslation) {
        toast({
          title: missingInDb
            ? `${code}: code text adopted as the database's English.`
            : `${code}: database English reverted to the code text.`,
        })
        onResolved()
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Could not write the text",
        description: error instanceof Error ? error.message : String(error),
      })
    }
    setIsArmed(false)
  }

  if (isArmed) {
    return (
      <span className="flex shrink-0 items-center gap-2">
        <span className="text-xs">
          Overwrite the database English with the code text?
        </span>
        <Button
          size="tiny"
          variant="destructive"
          data-feature-code="UI_BackofficeTranslation_Fallback_KeepCodeConfirm"
          onClick={onKeepCode}
          disabled={upsertState.loading}
          isLoading={upsertState.loading}
        >
          {upsertState.loading ? "Writing…" : "Yes, keep code text"}
        </Button>
        <Button
          size="tiny"
          variant="secondary"
          data-feature-code="UI_BackofficeTranslation_Fallback_KeepCodeCancel"
          onClick={() => setIsArmed(false)}
        >
          Cancel
        </Button>
      </span>
    )
  }

  return (
    <Button
      size="tiny"
      variant="secondary"
      className="shrink-0"
      data-feature-code="UI_BackofficeTranslation_Fallback_KeepCode"
      onClick={() => setIsArmed(true)}
      title={
        missingInDb
          ? "Create the missing database English from the code text (api.Upsert_Common_Translation)."
          : "Write the red code text into the database, discarding the database edit. Both sides become equal and nothing syncs."
      }
    >
      {missingInDb ? "Adopt code text into DB" : "Keep code text instead"}
    </Button>
  )
}

export default TranslationFallbackKeepCodeButton
